<template>
  <AppDialog
    show
    class="archive-consequences-dialog"
    position="centered"
    :title="t.dialogTitle"
    @close="closeDialog"
  >
    <div class="info">{{ t.dialogSubtitle }}</div>
    <div
      v-if="showUsageData"
      class="usage-info"
    >
      <div class="title">{{ t.dialogWarning }}</div>
      <div class="banner banner-lg d-flex align-items-center">
        <div class="items-removed">
          <div
            v-if="projectsAndTodosCaption"
            class="item item-projects-todos mb-8"
          >
            <i class="hi hi-remove" />
            <span>{{ projectsAndTodosCaption }}</span>
          </div>
          <div class="item item-screenshots mb-8">
            <i class="hi hi-remove" />
            <span>{{ screenshotsCaption }}</span>
          </div>
          <div
            v-if="paymentRecordsCaption"
            class="item item-payment-records mb-8"
          >
            <i class="hi hi-remove" />
            <span>{{ paymentRecordsCaption }}</span>
          </div>
          <div
            v-if="activitiesDurationCaption"
            class="item item-activities-duration mb-8"
          >
            <i class="hi hi-remove" />
            <span>{{ activitiesDurationCaption }}</span>
          </div>
          <div class="item item-productivity-metrics mb-8">
            <i class="hi hi-remove" />
            <span>{{ productivityMetricsCaption }}</span>
          </div>
        </div>
        <img
          class="illustration"
          :src="organizationAssets?.archive_flow?.banner_illustration_red.src"
        />
      </div>
    </div>
    <div
      v-if="showFeatures"
      class="features"
    >
      <div class="title">You {{ otherMembersCountCaption }}will also lose access to:</div>
      <ul class="list">
        <li
          v-for="feature in usageData.features"
          :key="feature"
        >
          {{ feature }}
        </li>
      </ul>
    </div>
    <div class="modal-footer d-flex justify-content-end pt-30 flex-wrap p-0">
      <a
        class="btn btn-default btn-default-hover-grey"
        @click="next"
      >
        {{ t.dialogTitle }}
      </a>
      <a
        class="btn btn-primary"
        @click="onKeepOrganizationClick"
      >
        Keep organization
      </a>
    </div>
  </AppDialog>
</template>

<script>
import { ORGANIZATION as organizationAssets } from '~/app_constants';
import { AnalyticsResource } from '~/resources';
import { pluralize } from '~/utils/string_utils';

const TEXT = {
  archive: {
    dialogTitle: 'Archive organization',
    dialogSubtitle: 'Are you sure you want to archive your organization and cancel your subscription?',
    dialogWarning: 'The following data will be immediately removed:',
  },
  cancel: {
    dialogTitle: 'Cancel subscription',
    dialogSubtitle: 'Are you sure you want to cancel your subscription?',
    dialogWarning: 'The following data will be removed immediately after the end of your current billing cycle:',
  }
};

export default {
  name: 'ArchiveConsequencesDialog',
  props: {
    organizationId: {
      type: Number,
      required: true,
    },
    usageData: {
      type: Object,
      required: true,
    },
    action: { type: String, default: 'archive' },
    source: { type: String, default: null },
  },
  data() {
    return {
      organizationAssets,
    };
  },
  computed: {
    activitiesDurationCaption() {
      const duration = this.usageData.activities_duration;
      const sentence = `${duration} of time and project reporting`;

      return duration ? sentence : null;
    },
    otherMembersCountCaption() {
      const sentence = `and ${this.usageData.members_count - 1} other members `;

      return this.usageData.members_count <= 1 ? '' : sentence;
    },
    paymentRecordsCaption() {
      const paymentRecordsCount = this.usageData.payment_records_count;
      const sentence = `${paymentRecordsCount} ${pluralize(paymentRecordsCount, 'Payment record', 'Payment records')}`;

      return paymentRecordsCount === 0 ? null : sentence;
    },
    productivityMetricsCaption() {
      const membersCount = this.usageData.members_count;

      return `Productivity and performance metrics for ${membersCount} ${pluralize(membersCount, 'member', 'members')}`;
    },
    projectsAndTodosCaption() {
      const captions = [];
      const projectsCount = this.usageData.projects_count;
      const todosCount = this.usageData.todos_count;

      if (projectsCount >= 1) {
        captions.push(`${projectsCount} ${pluralize(projectsCount, 'Project', 'Projects')}`);
      }
      if (todosCount >= 1) {
        captions.push(`${todosCount} ${pluralize(todosCount, 'To-do', 'To-dos')}`);
      }

      return captions.length === 0 ? null : captions.join(' and ');
    },
    screenshotsCaption() {
      const screenshotsCount = this.usageData.screenshots_count;
      const membersCount = this.usageData.members_count;
      const limittedScreenshotsCount = screenshotsCount >= 10001 ? '10000+' : screenshotsCount;
      let sentence;

      if (screenshotsCount === 0) {
        sentence = 'All recorded screenshots for ';
      } else {
        sentence = `${limittedScreenshotsCount} ${pluralize(screenshotsCount, 'Screenshot', 'Screenshots')} for `;
      }
      sentence += `${membersCount} ${pluralize(membersCount, 'member', 'members')}`;

      return sentence;
    },
    showFeatures() {
      return this.usageData?.features?.length > 0;
    },
    showUsageData() {
      return this.usageData && Object.keys(this.usageData).length > 0;
    },
    t() {
      return TEXT[this.action];
    },
  },
  methods: {
    next(event) {
      this.$emit('next', event);
    },
    closeDialog(event) {
      this.$emit('closeDialog', event);
    },
    onKeepOrganizationClick(event) {
      AnalyticsResource.create(null, {
        event_name: 'Keep organization clicked',
        event_properties: {
          organization_id: this.organizationId,
          action: this.action,
          ...(this.source && { source: this.source }),
        },
      }).catch((error) => console.error('Failed to track Keep organization clicked:', error));
      this.closeDialog(event);
    },
  },
};
</script>
